import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertTriangle,
  Cloud,
  Droplets,
  Loader2,
  MapPin,
  ShieldAlert,
  Wind,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DashboardPanel } from "@/components/dashboard/DashboardPanel";
import { cn } from "@/lib/utils";

type WeatherResponse = {
  location?: string;
  current: {
    temp_f: number;
    wind_mph: number;
    humidity?: number;
    condition: string;
  };
  forecast: Array<{
    date: string;
    icon: string;
    condition: string;
    max_temp_f: number;
    min_temp_f: number;
  }>;
};

export function StormWatchWidget() {
  const [weather, setWeather] = useState<WeatherResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("get-weather-forecast", {
          body: { location: "Chicago", days: 7 },
        });

        if (error) throw error;
        setWeather(data as WeatherResponse);
      } catch (error) {
        console.error("Error fetching weather:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  const hailRisk = useMemo(() => {
    if (!weather) return { level: "Low", tone: "text-primary", dot: "bg-primary", detail: "Quiet pattern" };

    const condition = weather.current.condition.toLowerCase();
    const windSpeed = weather.current.wind_mph;

    if ((condition.includes("storm") || condition.includes("thunder")) && windSpeed > 20) {
      return { level: "High", tone: "text-destructive", dot: "bg-destructive", detail: "Active storm setup" };
    }

    if ((condition.includes("rain") || condition.includes("showers")) && windSpeed > 15) {
      return { level: "Medium", tone: "text-warning", dot: "bg-warning", detail: "Monitor crews closely" };
    }

    return { level: "Low", tone: "text-success", dot: "bg-success", detail: "Low exposure today" };
  }, [weather]);

  if (loading || !weather) {
    return (
      <DashboardPanel
        eyebrow="Field weather"
        title="Storm Watch"
        description="Forecast intelligence for crews and canvassing"
        icon={<Cloud className="h-5 w-5" />}
        className="h-full"
      >
        <div className="flex min-h-[220px] items-center justify-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading forecast...
        </div>
      </DashboardPanel>
    );
  }

  const days = (weather.forecast || []).slice(0, 7);

  return (
    <DashboardPanel
      eyebrow="Field weather"
      title="Storm Watch"
      description="Forecast intelligence for crews and canvassing"
      icon={<Cloud className="h-5 w-5" />}
      className="h-full bg-gradient-to-br from-card via-card to-primary/5"
      contentClassName="space-y-5 p-5"
      action={
        <Badge variant="outline" className="border-border/70 bg-background/70 text-xs font-medium">
          <MapPin className="mr-1 h-3 w-3" />
          {typeof weather.location === "string" ? weather.location : (weather.location as any)?.name || "Chicago"}
        </Badge>
      }
    >
      <div className="grid gap-3 lg:grid-cols-[1.35fr_0.85fr]">
        <div className="rounded-xl border border-border/70 bg-background/70 p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Current conditions
              </p>
              <div className="mt-2 flex items-end gap-3">
                <span className="text-4xl font-semibold tracking-tight text-foreground">
                  {Math.round(weather.current.temp_f)}°
                </span>
                <span className="pb-1 text-sm text-muted-foreground">{weather.current.condition}</span>
              </div>
            </div>

            <div className="flex min-w-[170px] items-center gap-2 rounded-xl border border-border/70 bg-card px-3 py-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ShieldAlert className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Hail risk</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className={cn("h-2 w-2 rounded-full", hailRisk.dot)} />
                  <span className={cn("text-sm font-semibold", hailRisk.tone)}>{hailRisk.level}</span>
                  <span className="text-xs text-muted-foreground">{hailRisk.detail}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div className="rounded-xl border border-border/60 bg-card px-3 py-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Wind className="h-4 w-4" />
                <span className="text-xs uppercase tracking-[0.18em]">Wind</span>
              </div>
              <p className="mt-2 text-xl font-semibold text-foreground">{Math.round(weather.current.wind_mph)} mph</p>
            </div>

            <div className="rounded-xl border border-border/60 bg-card px-3 py-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Droplets className="h-4 w-4" />
                <span className="text-xs uppercase tracking-[0.18em]">Humidity</span>
              </div>
              <p className="mt-2 text-xl font-semibold text-foreground">{weather.current.humidity ?? 0}%</p>
            </div>

            <div className="rounded-xl border border-border/60 bg-card px-3 py-3 sm:col-span-2 xl:col-span-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs uppercase tracking-[0.18em]">Operations</span>
              </div>
              <p className="mt-2 text-sm font-medium text-foreground">
                {hailRisk.level === "High"
                  ? "Prep response crews and inspect active claims."
                  : hailRisk.level === "Medium"
                    ? "Watch schedule shifts and outbound follow-up."
                    : "Good day for inspections and canvassing."}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border/70 bg-background/70 p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Forecast</p>
            <span className="text-xs text-muted-foreground">{days.length} days</span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
            {days.map((day, index) => (
              <div
                key={day.date}
                className="flex items-center justify-between rounded-xl border border-border/60 bg-card px-3 py-3 transition-colors hover:bg-accent/60"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <img src={`https:${day.icon}`} alt={day.condition} className="h-9 w-9 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      {index === 0
                        ? "Today"
                        : new Date(day.date).toLocaleDateString("en-US", { weekday: "short" })}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{day.condition}</p>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm font-semibold text-foreground">{Math.round(day.max_temp_f)}°</p>
                  <p className="text-xs text-muted-foreground">{Math.round(day.min_temp_f)}° low</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardPanel>
  );
}